/* import __COLOCATED_TEMPLATE__ from './fin-incentive-modal.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import { post } from 'embercom/lib/ajax';
import type QuoteService from 'embercom/services/quote-service';
import { DEFAULT_REQUEST_PARAMS } from 'embercom/services/quote-service';

export interface Args {
  openModal: boolean;
  refreshOfferStateAndRedirect: Task<void, any>;
}

export default class FinIncentiveModal extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare quoteService: QuoteService;
  @service declare customerService: $TSFixMe;
  @tracked isOpen = this.args.openModal;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.trackAnalyticsEvent({
      action: 'viewed',
      object: 'fin_incentive_modal',
      place: 'fin_incentive_modal',
    });
    taskFor(this.loadQuotes).perform();
  }

  @task({ drop: true })
  *loadQuotes() {
    yield taskFor(this.quoteService.getQuotes).perform([
      {
        ...DEFAULT_REQUEST_PARAMS,
        planIds: [this.activeCorePlanId],
        source: 'fin-incentive-modal',
      },
    ]);
  }

  get isLoading() {
    return taskFor(this.loadQuotes).isRunning || this.quoteService.loading;
  }

  get activeCorePlanId() {
    return this.customerService.activePricing5CorePlanId;
  }

  get finResolutionsPrice() {
    let quote = this.quoteService.customerSubscriptionPrice;
    return quote?.finMetricPrice(this.activeCorePlanId);
  }

  get claimTaskIsRunning() {
    return taskFor(this.claimFreeResolutions).isRunning;
  }

  @task({ drop: true })
  *claimFreeResolutions() {
    this.trackAnalyticsEvent({
      action: 'clicked',
      object: 'claim_free_fin_resolutions',
      place: 'fin_incentive_modal',
    });

    try {
      yield post('/ember/customers/claim_fin_incentive', {
        app_id: this.appService.app.id,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('incentive-offer.fin-incentive-modal.notifications.success_message'),
      );

      this.trackAnalyticsEvent({
        action: 'success',
        object: 'claim_free_fin_resolutions',
        place: 'fin_incentive_modal',
      });
      this.isOpen = false;
      this.args.refreshOfferStateAndRedirect.perform();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('incentive-offer.fin-incentive-modal.notifications.error_message'),
      );
    }
  }

  private trackAnalyticsEvent(eventData: Record<string, string>) {
    this.intercomEventService.trackAnalyticsEvent(eventData);
  }

  @action
  closeModal() {
    this.trackAnalyticsEvent({
      action: 'clicked',
      object: 'close_fin_incentive_modal',
      place: 'fin_incentive_modal',
    });
    this.isOpen = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'IncentiveOffer::FinIncentiveModal': typeof FinIncentiveModal;
    'incentive-offer/fin-incentive-modal': typeof FinIncentiveModal;
  }
}
